import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { productsAPI } from '../services/api';
import toast from 'react-hot-toast';

// Image URL helper function - HTTPS uyumlu
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // Production'da HTTPS kullan
  if (process.env.NODE_ENV === 'production') {
    // HTTPS ile başlamıyorsa HTTPS ekle
    if (imagePath.startsWith('http://')) {
      return imagePath.replace('http://', 'https://');
    }
    // HTTPS ile başlıyorsa direkt döndür
    if (imagePath.startsWith('https://')) {
      return imagePath;
    }
    // Relative path ise HTTPS domain ile birleştir
    if (imagePath.startsWith('/')) {
      return `https://leasertech.com${imagePath}`;
    }
    return `https://leasertech.com/${imagePath}`;
  }
  
  // Development'ta localhost kullan
  const API_BASE_URL = 'http://localhost:5000';
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${API_BASE_URL}${imagePath}`;
};

const Favorites = () => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadFavorites();
    }
  }, [user]);

  const loadFavorites = async () => {
    try {
      // Mock favoriler - localStorage'dan yükle
      const savedFavorites = localStorage.getItem('favorites');
      if (savedFavorites) {
        const favoriteIds = JSON.parse(savedFavorites);
        
        // Favori ürün ID'lerini kullanarak ürün bilgilerini getir
        const favoriteProducts = [];
        for (const productId of favoriteIds) {
          try {
            const response = await productsAPI.getProduct(productId);
            favoriteProducts.push({
              id: productId,
              product: response.data.product
            });
          } catch (error) {
            console.error(`Ürün ${productId} yüklenemedi:`, error);
          }
        }
        
        setFavorites(favoriteProducts);
      }
    } catch (error) {
      console.error('Favoriler yüklenemedi:', error);
      toast.error('Favoriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (productId) => {
    try {
      // Mock favori çıkarma - localStorage'dan çıkar
      const savedFavorites = localStorage.getItem('favorites');
      if (savedFavorites) {
        const favoriteIds = JSON.parse(savedFavorites);
        const updatedFavorites = favoriteIds.filter(id => id !== productId);
        localStorage.setItem('favorites', JSON.stringify(updatedFavorites));
        
        setFavorites(favorites.filter(fav => fav.product.id !== productId));
        toast.success('Ürün favorilerden çıkarıldı');
      }
    } catch (error) {
      console.error('Favori çıkarılamadı:', error);
      toast.error('Ürün favorilerden çıkarılamadı');
    }
  };

  const handleAddToCart = async (product) => {
    try {
      await addToCart(product.id, 1);
      toast.success('Ürün sepete eklendi');
    } catch (error) {
      console.error('Sepete eklenemedi:', error);
      toast.error('Ürün sepete eklenemedi');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Favorilerim
            </h1>
            <p className="text-gray-600">
              Favorilerinizi görmek için giriş yapmanız gerekiyor.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Favoriler yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Favorilerim</h1>
          <p className="mt-2 text-gray-600">
            Beğendiğiniz ürünleri burada bulabilirsiniz
          </p>
        </div>

        {favorites.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Henüz favori ürününüz yok
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Beğendiğiniz ürünleri favorilere ekleyebilirsiniz.
            </p>
            <div className="mt-6">
              <Link
                to="/"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Alışverişe Başla
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favorites.map((favorite) => (
              <div key={favorite.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col h-full">
                <Link to={`/product/${favorite.product.id}`} className="block flex-1 flex flex-col">
                  <div className="aspect-square bg-gray-100 p-4 flex-shrink-0">
                    {favorite.product.images && favorite.product.images.length > 0 ? (
                      <img
                        src={getImageUrl(favorite.product.images[0].url)}
                        alt={favorite.product.name}
                        className="w-full h-full object-contain"
                        crossOrigin="anonymous"
                        loading="lazy"
                        onError={(e) => {
                          console.error('Görsel yüklenemedi:', favorite.product.name, favorite.product.images[0].url);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center" style={{display: 'none'}}>
                      <span className="text-gray-400">Görsel Yok</span>
                    </div>
                  </div>
                  
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 line-clamp-2 hover:text-blue-600 mb-2 min-h-[2.5rem]">
                        {favorite.product.name}
                      </h3>
                      
                      <div className="mb-2 flex items-center">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < (favorite.product.rating_average || 0)
                                  ? 'text-yellow-400 fill-current'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                          <span className="ml-1 text-sm text-gray-500">
                            ({favorite.product.rating_count || 0})
                          </span>
                        </div>
                      </div>
                      
                      <div className="mb-3 flex items-center space-x-2">
                        <span className="text-lg font-bold text-gray-900">
                          ₺{parseFloat(favorite.product.price || 0).toLocaleString()}
                        </span>
                        {favorite.product.original_price && 
                         parseFloat(favorite.product.original_price) > parseFloat(favorite.product.price) && (
                          <span className="text-sm text-gray-500 line-through">
                            ₺{parseFloat(favorite.product.original_price).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
                
                <div className="p-4 pt-0 flex-shrink-0">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleAddToCart(favorite.product)}
                      className="flex-1 bg-green-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-green-700 flex items-center justify-center"
                    >
                      <ShoppingCart className="h-4 w-4 mr-1" />
                      Sepete Ekle
                    </button>
                    <button
                      onClick={() => handleRemoveFavorite(favorite.product.id)}
                      className="bg-red-100 text-red-600 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-200 flex items-center justify-center"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Favorites;
